import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { TitleSubTitle } from "@platformx/utilities";
import { useEffect, useState } from "react";
import { useStyles } from "./ContentStatus.styles";

interface ContentStatusOption {
  value: string;
  label: string;
}

interface ContentStatusProps {
  titleSubTitles: {
    contentStatusTitle: string;
    contentStatusSubTitle: string;
    contentStatusFieldName: string;
  };
  state: any;
  setState: (updater: (prevState: any) => any) => void;
  disabled?: boolean;
  required?: boolean;
  options?: ContentStatusOption[];
}

const ContentStatus: React.FC<ContentStatusProps> = ({
  titleSubTitles,
  state,
  setState,
  disabled = false,
  required = false,
  options = [],
}) => {
  const classes = useStyles();
  const { contentStatusTitle, contentStatusSubTitle, contentStatusFieldName } = titleSubTitles;

  // Default content status options
  const defaultOptions: ContentStatusOption[] = [
    {
      value: "Free",
      label: "Free",
    },
    {
      value: "Behind Login",
      label: "Behind Login",
    },
    {
      value: "Behind Subscription",
      label: "Behind Subscription",
    },
  ];

  // Use provided options or fall back to defaults
  const contentStatusOptions = options.length > 0 ? options : defaultOptions;

  // Local state for content status selection
  const [selectedContentStatus, setSelectedContentStatus] = useState<string>("");

  // Check if this is VOD content type or regular content
  const isVod = state?.CommonFields?.category === "Vod";

  // Sync local state with main state
  useEffect(() => {
    const currentValue = isVod
      ? state?.CommonFields?.[contentStatusFieldName]
      : state?.CommonFields?.[contentStatusFieldName];

    if (currentValue && currentValue !== selectedContentStatus) {
      setSelectedContentStatus(currentValue);
    }
  }, [state, contentStatusFieldName, isVod, selectedContentStatus]);

  // Handle dropdown change - following NotificationSetting pattern
  const handleContentStatusChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    setSelectedContentStatus(value);
    handleDropdownChange({
      target: {
        name: contentStatusFieldName,
        value: value,
      },
    });
  };

  // Handle state updates - following NotificationSetting pattern
  const handleDropdownChange = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;

    setState((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          [name]: value,
        },
      };
    });
  };

  // Get current value for display - following NotificationSetting pattern
  const getCurrentValue = (): string => {
    const currentValue = state?.CommonFields?.[contentStatusFieldName];
    return currentValue || selectedContentStatus || contentStatusOptions[0]?.value || "";
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.fullrowItem}>
        <TitleSubTitle
          title={contentStatusTitle}
          subTitle={contentStatusSubTitle}
          titleVariant='h6medium'
          subTitleVariant='p4regular'
        />
        <Box className={classes.gap}>
          <FormControl fullWidth disabled={disabled} required={required}>
            <Select
              labelId='content-status-label'
              id='content-status-select'
              value={getCurrentValue()}
              onChange={handleContentStatusChange}
              input={<OutlinedInput />}
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  paddingY: "12px",
                },
              }}>
              {contentStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box component='span'>{option.label}</Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ContentStatus;
